import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, ExternalLink } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, friendlyError } from '@/lib/api';
import { toast } from 'sonner';

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchNotifications({ limit: 15 })); }
    catch { /* silent - unauth users no ven la campanita */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // polling 60s
    return () => clearInterval(t);
  }, [load]);

  const onOpen = async (o) => {
    setOpen(o);
    if (o) await load();
  };

  const handleRead = async (id) => {
    try {
      await markNotificationRead(id);
      await load();
    } catch (err) { toast.error(friendlyError(err)); }
  };
  // Fase 09 · Al abrir una notificación, cerrar dropdown Y marcarla como leída.
  const handleOpen = async (n) => {
    setOpen(false);
    if (!n?.read_at) {
      try { await markNotificationRead(n.id); await load(); }
      catch { /* silent — la navegación no se debe bloquear por esto */ }
    }
  };
  const handleReadAll = async () => {
    try {
      const res = await markAllNotificationsRead();
      toast.success(res.message || 'Notificaciones marcadas.');
      await load();
    } catch (err) { toast.error(friendlyError(err)); }
  };

  const unread = data?.unread_count || 0;
  const items = data?.items || [];

  return (
    <DropdownMenu open={open} onOpenChange={onOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-xl text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-gold)]/40"
          aria-label="Notificaciones"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 ? (
            <span data-testid="notifications-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[var(--ns-red)] text-white text-[10px] font-bold">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--ns-border)]">
          <p className="font-semibold text-sm text-[var(--ns-text)]">Notificaciones</p>
          {unread > 0 ? (
            <Button variant="ghost" size="sm" onClick={handleReadAll} data-testid="notifications-read-all" className="h-7 text-xs">
              <Check className="h-3 w-3 mr-1" /> Marcar todas
            </Button>
          ) : null}
        </div>
        <div className="flex-1 overflow-auto">
          {loading && items.length === 0 ? (
            <p className="p-4 text-xs text-[var(--ns-text-muted)] text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <p data-testid="notifications-empty" className="p-6 text-xs text-[var(--ns-text-muted)] text-center">
              No tienes notificaciones.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--ns-border)]">
              {items.map((n) => {
                const created = n.created_at ? new Date(n.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <li key={n.id} data-testid={`notification-${n.id}`} className={`p-3 hover:bg-[var(--ns-bg-2)]/60 ${!n.read_at ? 'bg-[var(--ns-amber-50)]/40' : ''}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{n.title}</p>
                        {n.body ? <p className="text-xs text-[var(--ns-text-muted)] truncate mt-0.5">{n.body}</p> : null}
                        <p className="text-[10px] text-[var(--ns-text-subtle)] mt-1">{created}</p>
                      </div>
                      {!n.read_at ? <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[9px] uppercase">Nueva</Badge> : null}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {n.link_to ? (
                        <Link to={n.link_to} onClick={() => handleOpen(n)} className="inline-flex items-center gap-1 text-xs text-[var(--ns-gold-700)] hover:underline">
                          Abrir <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : null}
                      {!n.read_at ? (
                        <button type="button" onClick={() => handleRead(n.id)} className="text-xs text-[var(--ns-text-muted)] hover:text-[var(--ns-text)]">
                          Marcar leída
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
